import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardBody, Col, Row, Label, FormGroup, Button, CardFooter } from "reactstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import TransitionOnLoad from "Components/TransitionOnLoad";
import dayjs from "dayjs";
import { ReactComponent as Small_Arrow } from "../../../../assets/utils/images/svgs/arrow-left.svg";

// Components
import AppFooter from "Layout/AppFooter";
import AppHeader from "../../../../Layout/AppHeader";
import ThemeOptions from "../../../../Layout/ThemeOptions";
import CustomCalendar from "Components/Calendar";
import DefaultButton from "Components/Buttons/DefaultButton";
import PageHeading from "Layout/AppMain/PageHeading";
import SweetAlert from "react-bootstrap-sweetalert";
import { useRequestDetails, useUpdateRequisition } from "Api/Melder/Substitution/Calender";

const weekDaysListWOWeekend = [
  "Maandag",
  "Dinsdag",
  "Woensdag",
  "Donderdag",
  "Vrijdag",
];

const Calendar = () => {
  const [datesState, setDatesState] = useState([]);
  const [newDates, setNewDates] = useState([]);
  const [gymList, setGymList] = useState([]);
  const [gymData, setGymData] = useState([]);
  const [formState, setFormState] = useState({ dateRange: [] });
  const [showModal, setShowModal] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const navigate = useNavigate();
  const params = useParams();
  const substitutionId = params.id || "";

  const { data: requestResponse } = useRequestDetails(substitutionId);
  const { trigger: updateRequisitionTrigger } = useUpdateRequisition();

  const requestData = requestResponse || null;

  // ---------------- helpers ----------------
  const getGymFromDates = (dates = []) => {
    const gym = [];

    dates.forEach((day) => {
      const weekDayNum = dayjs(day.date).day();

      if (weekDayNum === 0 || weekDayNum === 6) return;

      let dayIds = [];
      let shifts = [];

      if (day.dayPart === "ochtend") {
        dayIds = [weekDayNum * 2 - 1];
        shifts = [" ochtend"];
      } else if (day.dayPart === "middag") {
        dayIds = [weekDayNum * 2];
        shifts = [" middag"];
      } else {
        dayIds = [weekDayNum * 2 - 1, weekDayNum * 2];
        shifts = [" ochtend", " middag"];
      }

      const gymObj = shifts.map((shift, index) => ({
        id: dayIds[index],
        day: `${weekDaysListWOWeekend[weekDayNum - 1]}${shift}`,
      }));

      gymObj.forEach((item) => {
        if (!gym.some((g) => g.day === item.day)) {
          gym.push(item);
        }
      });
    });

    return gym.sort((a, b) => a.id - b.id);
  };

  const upsertDate = (list, newItem) => {
    const filtered = list.filter((item) => item.date !== newItem.date);
    return [...filtered, newItem];
  };

  const removeDateByValue = (list, date) => {
    return list.filter((item) => item.date !== date);
  };

  // ---------------- initial data fill ----------------
  useEffect(() => {
    if (!requestData) return;
    if (requestData.status === false && requestData.error === "invalid_link") {
      navigate("/no-page-found");
      return;
    }

    const convertedDayparts = requestData?.converted_dayparts || [];

    const datesData = convertedDayparts.map((dateElem) => {
      const [day, month, year] = dateElem.day.split("-");
      const dateValue = `${year}/${month}/${day}`;

      return {
        date: dateValue,
        dayPart:
          Object.keys(dateElem.data || {}).length === 2
            ? "full"
            : Object.values(dateElem.data || {})[0]?.vShift?.toLowerCase(),
      };
    });

    setDatesState(datesData);
    setGymData(getGymFromDates(datesData));

    const resultInfo = requestData?.result_info || {};

    setFormState((prev) => ({
      ...prev,
      organisationId: resultInfo?.code?.iCommunityId || "",
      schoolId: resultInfo?.school?.schoolId || "",
      absentId: resultInfo?.iAbsentAdminId || "",
      groupsId: (resultInfo?.groups || []).map((e) => e.iSubGroupId),
      codeId: resultInfo?.iCodeId || "",
      type: resultInfo?.eType || "",
      planning: resultInfo?.iPlanOption || "",
      label: resultInfo?.tExtra || "",
      iHowToInformId: resultInfo?.iHowToInformId || "",
      informEmailExtra1: resultInfo?.vExtraEmail1 || "",
      informEmailExtra2: resultInfo?.vExtraEmail2 || "",
      substitutionNote: resultInfo?.vCoOrdComments || "",
      schedulerNote: resultInfo?.vDirComments || "",
      user: "melder",
      gym: [],
      dateRange: prev.dateRange || [],
    }));
  }, [requestData, navigate]);

  // keep formState.gym synced with selected gym ids
  useEffect(() => {
    setFormState((prev) => ({
      ...prev,
      gym: [...gymList].sort((a, b) => a - b),
    }));
  }, [gymList]);

  // update gym dropdown data when user changes dates
  useEffect(() => {
    const mergedDates = [...datesState, ...newDates];
    const uniqueDates = mergedDates.filter(
      (item, index, arr) =>
        index === arr.findIndex((x) => x.date === item.date && x.dayPart === item.dayPart)
    );

    setGymData(getGymFromDates(uniqueDates));
  }, [datesState, newDates]);

  // ---------------- handlers ----------------
  const handleSubmit = async () => {
    setIsDisabled(true);

    if (!formState.dateRange || formState.dateRange.length === 0) {
      setShowModal(true);
      setIsDisabled(false);
      return;
    }

    try {
      const response = await updateRequisitionTrigger({
        url: `requisition/${substitutionId}`,
        data: formState,
      });

      // depending on your custom hook response shape
      if (response?.status === "success") {
        navigate(`/verzoeken/${substitutionId}`);
        return;
      }

      setIsDisabled(false);
    } catch (err) {
      console.error(err);
      setIsDisabled(false);
    }
  };

  const manageDays = (daysElemArray) => {
    const daysElemArrayWOSelected = daysElemArray.filter(
      (dayElem) => !dayElem.classList.contains("selected")
    );

    const checked = (className) =>
      daysElemArrayWOSelected.every((dayElem) =>
        dayElem.classList.contains(className)
      );

    let updatedDates = [...newDates];

    daysElemArrayWOSelected.forEach((dayElem) => {
      const date = dayElem.attributes.date.value;

      updatedDates = updatedDates.filter((value) => value.date !== date);

      if (checked("bg-success")) {
        updatedDates.push({ date, dayPart: "ochtend" });
      } else if (checked("bg-warning")) {
        updatedDates.push({ date, dayPart: "middag" });
      } else if (checked("bg-danger")) {
        // remove only, nothing to add
      } else {
        updatedDates.push({ date, dayPart: "full" });
      }
    });

    setNewDates(updatedDates);
  };

  const manageDate = (day) => {
    const updatedDates = upsertDate([...newDates], day);
    setNewDates(updatedDates);
  };

  const removeDate = (day) => {
    const updatedDates = removeDateByValue([...newDates], day);
    setNewDates(updatedDates);
  };

  const selectedGymOptions = useMemo(() => {
    return gymData.filter((item) => gymList.includes(item.id));
  }, [gymData, gymList]);

  return (
    <Fragment>
      <AppHeader />
      <ThemeOptions />
        <TransitionOnLoad>
          <div>
            <div className="chat-layout-wrapper">
              <div className="app-inner-layout chat-layout">

                <div className="app-inner-layout__wrapper">
                  <Card className="app-inner-layout__content">
                    <Row>
                      <Col md="12">
                        <Card className="main-card mb-3">
                          <CardBody className="p-3">
                            <CustomCalendar
                              removeDate={removeDate}
                              manageDays={manageDays}
                              manageDate={manageDate}
                              preselectedData={datesState}
                              prevState={formState}
                              setState={setFormState}
                              weekDays={weekDaysListWOWeekend}
                              isDisabled={isDisabled}
                            />

                            <div >
                              <FormGroup row className="custom-pl-0 custom-ml-0 mt-5 gym-input-style">
                                <Row>


                                  <Col md="3" sm={3}>

                                    <Label className="d-block w-100 gym-text-right ms-3 mt-2" >
                                      Gym
                                    </Label>
                                  </Col>

                                  <Col md="5" sm={9}>
                                    <Typeahead
                                      id="gym"
                                      name="gym"
                                      labelKey="day"
                                      options={gymData}
                                      multiple
                                      selected={selectedGymOptions}
                                      placeholder="Kies gym dagen"
                                      onChange={(selected) =>
                                        setGymList(selected.map((item) => item.id))
                                      }
                                    />
                                  </Col>

                                </Row>
                              </FormGroup>
                            </div>

                          
                            <SweetAlert
                              title="Selecteer minimaal 1 dagdeel"
                              show={showModal}
                              type="warning"
                              onConfirm={() => {
                                setShowModal(false);
                                setIsDisabled(false);
                              }}
                              confirmBtnText={"Sluiten"}
                            />
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </TransitionOnLoad>

        <div className="chat-layout-footer">
          <CardFooter className="d-block text-center d-flex chat-layout-footer__inner justify-content-end">

            <div className="d-flex justify-content-end">
              <Button
                className="d-flex align-items-center btn-color-style back-btn"
                size="sm"
                outline
                color="primary me-2"
                onClick={() => navigate(-1)}
              >
                Annuleren
              </Button>

              <DefaultButton
                onClick={handleSubmit}
                type="button"
                className="ms-2 float-end btn-color-style"
                disabled={isDisabled}
              >
                Toevoegen
              </DefaultButton>
            </div>

          </CardFooter>
        </div>
        {/* <AppFooter /> */}
    </Fragment>
  );
};

export default Calendar;

